// TennisTrack Hub — Dettaglio Allenamento + Video YouTube | 029 Project
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import './Allenamenti.css';

// Componente embed YouTube
const YouTubeEmbed = ({ youtubeId, titolo }) => (
  <div className="yt-embed-wrapper">
    <iframe
      src={`https://www.youtube.com/embed/${youtubeId}`}
      title={titolo || 'Video allenamento'}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      className="yt-embed"
    />
  </div>
);

const DettaglioAllenamento = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { utente } = useAuth();
  const [allenamento, setAllenamento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Form aggiunta video
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [videoForm, setVideoForm] = useState({ titolo: '', youtubeUrl: '', note: '' });
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoErrore, setVideoErrore] = useState('');

  const caricaAllenamento = useCallback(async () => {
    try {
      const res = await api.get(`/allenamenti/${id}`);
      setAllenamento(res.data.allenamento);
    } catch {
      navigate('/atleti');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { caricaAllenamento(); }, [caricaAllenamento]);

  const aggiungiVideo = async (e) => {
    e.preventDefault();
    setVideoLoading(true);
    setVideoErrore('');
    try {
      await api.post(`/allenamenti/${id}/video`, videoForm);
      setVideoForm({ titolo: '', youtubeUrl: '', note: '' });
      setShowVideoForm(false);
      caricaAllenamento();
    } catch (err) {
      setVideoErrore(err.response?.data?.error || 'URL non valido.');
    } finally {
      setVideoLoading(false);
    }
  };

  const eliminaVideo = async (videoId) => {
    if (!window.confirm('Eliminare questo video?')) return;
    try {
      await api.delete(`/allenamenti/video/${videoId}`);
      caricaAllenamento();
    } catch (err) {
      console.error(err);
    }
  };

  const stelle = (n) => '★'.repeat(n) + '☆'.repeat(5 - n);

  const scaricaPdf = async () => {
    if (utente?.piano !== 'premium') {
      navigate('/premium');
      return;
    }
    setPdfLoading(true);
    try {
      const res = await api.get(`/pdf/allenamento/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `allenamento_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Errore nella generazione del PDF.');
      console.error(err);
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) return <div className="page-layout"><Navbar /><div className="loading-msg">Caricamento...</div></div>;
  if (!allenamento) return null;

  const isTennis = allenamento.tipo === 'tennis';

  return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main">

        {/* Header */}
        <div className="page-header">
          <div>
            <h1>{isTennis ? '🎾' : '💪'} Allenamento {allenamento.tipo}</h1>
            <p>{new Date(allenamento.data).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              className="btn btn-secondary"
              onClick={scaricaPdf}
              disabled={pdfLoading}
              title={utente?.piano !== 'premium' ? 'Funzione Premium — clicca per fare upgrade' : 'Scarica PDF'}
            >
              {pdfLoading ? '⏳ PDF...' : utente?.piano === 'premium' ? '📄 PDF' : '🔒 PDF'}
            </button>
            <button className="btn btn-secondary" onClick={() => navigate(`/atleti/${allenamento.atleta_id}`)}>
              ← Atleta
            </button>
          </div>
        </div>

        {/* Info sessione */}
        <div className="card dettaglio-header">
          {allenamento.valutazione > 0 && (
            <div className="dettaglio-valutazione">
              <span className="stelle-display">{stelle(allenamento.valutazione)}</span>
              <span className="stelle-num">{allenamento.valutazione}/5</span>
            </div>
          )}
          {allenamento.obiettivi && (
            <div className="dettaglio-obiettivi">
              <span className="info-label">🎯 Obiettivi</span>
              <p>{allenamento.obiettivi}</p>
            </div>
          )}
          {allenamento.note && (
            <div className="dettaglio-note">
              <span className="info-label">📝 Note generali</span>
              <p>{allenamento.note}</p>
            </div>
          )}
        </div>

        {/* Esercizi Tennis */}
        {isTennis && allenamento.esercizi_tennis?.length > 0 && (
          <div className="card dettaglio-section">
            <h3 className="section-title">🎾 Esercizi Tennis ({allenamento.esercizi_tennis.length})</h3>
            <div className="esercizi-dettaglio">
              {allenamento.esercizi_tennis.map((es, i) => (
                <div key={es.id} className="esercizio-dettaglio-item">
                  <div className="ed-header">
                    <span className="ed-num">#{i + 1}</span>
                    <strong className="ed-colpo">{es.colpo}</strong>
                    {es.difficolta && (
                      <div className="ed-difficolta">
                        <div className="diff-bar">
                          <div className="diff-fill" style={{ width: `${(es.difficolta / 10) * 100}%` }} />
                        </div>
                        <span>{es.difficolta}/10</span>
                      </div>
                    )}
                  </div>
                  {es.descrizione && <p className="ed-descrizione">{es.descrizione}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Esercizi Fitness */}
        {!isTennis && allenamento.esercizi_fitness?.length > 0 && (
          <div className="card dettaglio-section">
            <h3 className="section-title">💪 Esercizi Fitness ({allenamento.esercizi_fitness.length})</h3>
            <div className="esercizi-dettaglio">
              {allenamento.esercizi_fitness.map((es, i) => (
                <div key={es.id} className="esercizio-dettaglio-item">
                  <div className="ed-header">
                    <span className="ed-num">#{i + 1}</span>
                    <strong className="ed-nome">{es.nome}</strong>
                    <span className={`tipologia-badge tipologia-${es.tipologia}`}>{es.tipologia?.replace('_', ' ')}</span>
                  </div>
                  <div className="ed-fitness-stats">
                    {es.serie && <span>📊 {es.serie} serie</span>}
                    {es.ripetizioni && <span>🔁 {es.ripetizioni} rip.</span>}
                    {es.carico_kg && <span>⚖️ {es.carico_kg} kg</span>}
                  </div>
                  {es.note && <p className="ed-descrizione">{es.note}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sezione Video */}
        <div className="card dettaglio-section">
          <div className="section-header-inline">
            <h3 className="section-title">📹 Video ({allenamento.video?.length || 0})</h3>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setShowVideoForm(!showVideoForm)}
            >
              {showVideoForm ? '✕ Annulla' : '+ Aggiungi video'}
            </button>
          </div>

          {/* Form aggiunta video */}
          {showVideoForm && (
            <form onSubmit={aggiungiVideo} className="video-form">
              {videoErrore && <div className="alert alert-error">{videoErrore}</div>}
              <div className="form-group">
                <label>Link YouTube *</label>
                <input
                  type="url"
                  placeholder="https://youtu.be/... oppure https://www.youtube.com/watch?v=..."
                  value={videoForm.youtubeUrl}
                  onChange={e => setVideoForm({ ...videoForm, youtubeUrl: e.target.value })}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Titolo (opzionale)</label>
                  <input
                    type="text"
                    placeholder="es. Servizio slice di Andrea"
                    value={videoForm.titolo}
                    onChange={e => setVideoForm({ ...videoForm, titolo: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Note (opzionale)</label>
                  <input
                    type="text"
                    placeholder="es. Osserva il toss..."
                    value={videoForm.note}
                    onChange={e => setVideoForm({ ...videoForm, note: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary btn-sm" disabled={videoLoading}>
                  {videoLoading ? 'Salvo...' : '💾 Salva video'}
                </button>
              </div>
            </form>
          )}

          {/* Lista video */}
          {allenamento.video?.length === 0 && !showVideoForm && (
            <div className="empty-state-small">
              <p>Nessun video per questo allenamento. Chiedi all'atleta di caricare il video su YouTube e incolla il link qui.</p>
            </div>
          )}

          <div className="video-grid">
            {allenamento.video?.map(v => (
              <div key={v.id} className="video-item">
                {v.youtube_id ? (
                  <YouTubeEmbed youtubeId={v.youtube_id} titolo={v.titolo} />
                ) : (
                  <div className="yt-no-embed">
                    <a href={v.youtube_url} target="_blank" rel="noopener noreferrer">
                      🎬 Apri su YouTube
                    </a>
                  </div>
                )}
                <div className="video-info">
                  {v.titolo && <strong>{v.titolo}</strong>}
                  {v.note && <p>{v.note}</p>}
                  <button
                    className="btn-text-danger"
                    onClick={() => eliminaVideo(v.id)}
                  >🗑 Rimuovi</button>
                </div>
              </div>
            ))}
          </div>
        </div>

      </main>
    </div>
  );
};

export default DettaglioAllenamento;
